export const EXTERNAL_CHAT_ACCESS_MODE_DISABLED = "DISABLED";
export const EXTERNAL_CHAT_ACCESS_MODE_PUBLIC = "PUBLIC";
export const EXTERNAL_CHAT_ACCESS_MODE_EMAIL_MAGIC_LINK = "EMAIL_MAGIC_LINK";
export const EXTERNAL_CHAT_ACCESS_MODE_AUTHENTICATED_USERS =
  "AUTHENTICATED_USERS";
export const EXTERNAL_CHAT_ACCESS_MODE_AUTHENTICATED_SELECTED_USERS =
  "AUTHENTICATED_SELECTED_USERS";

export const EXTERNAL_CHAT_ACCESS_MODES = new Set([
  EXTERNAL_CHAT_ACCESS_MODE_DISABLED,
  EXTERNAL_CHAT_ACCESS_MODE_PUBLIC,
  EXTERNAL_CHAT_ACCESS_MODE_EMAIL_MAGIC_LINK,
  EXTERNAL_CHAT_ACCESS_MODE_AUTHENTICATED_USERS,
  EXTERNAL_CHAT_ACCESS_MODE_AUTHENTICATED_SELECTED_USERS,
]);

export const EXTERNAL_CHAT_DEFAULT_ACCESS_POLICY = {
  mode: EXTERNAL_CHAT_ACCESS_MODE_DISABLED,
  emailRestriction: "ANY",
  allowedEmails: [],
  allowedDomains: [],
  selectedUserIds: [],
};

export const EXTERNAL_CHAT_MAX_SESSIONS_PER_IDENTITY_PRESETS = [
  1, 3, 5, 10, 25, 50, 100, 250, 500, 1000,
];

export const EXTERNAL_CHAT_MAX_SESSIONS_TOTAL_PRESETS = [
  10, 25, 50, 100, 250, 500, 1000, 5000, 10000, 50000, 100000,
];

export const EXTERNAL_CHAT_RATE_LIMIT_PERIOD_UNITS = [
  { value: "minutes", label: "minutes", seconds: 60 },
  { value: "hours", label: "hours", seconds: 3600 },
  { value: "days", label: "days", seconds: 86400 },
];

export const EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_UNIT = "hours";
export const EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_VALUE = "1";
export const EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_EXCEEDED_MESSAGE =
  "You've reached the message limit for now. Please try again later.";

export const EXTERNAL_CHAT_ACCESS_MODE_OPTIONS = [
  {
    value: EXTERNAL_CHAT_ACCESS_MODE_DISABLED,
    label: "Disabled (private only)",
    menuHint: "No public link access.",
    description: "Public chat link access is fully turned off.",
    detailLines: [
      "No end user can start or continue chat through a public link.",
      "Use this when the agent should stay private/internal only.",
    ],
  },
  {
    value: EXTERNAL_CHAT_ACCESS_MODE_PUBLIC,
    label: "Public (anyone with link)",
    menuHint: "No sign-in required.",
    description: "Anyone with the link can start a chat, subject to limits.",
    detailLines: [
      "No sign-in required.",
      "Identity is tracked as anonymous browser ID (with IP fallback).",
    ],
  },
  {
    value: EXTERNAL_CHAT_ACCESS_MODE_EMAIL_MAGIC_LINK,
    label: "Email verification (magic link)",
    menuHint: "User must verify email first.",
    description: "Users verify their email before they can start a chat.",
    detailLines: [
      "No account required.",
      "Each user must verify email by one-time magic link first.",
    ],
  },
  {
    value: EXTERNAL_CHAT_ACCESS_MODE_AUTHENTICATED_USERS,
    label: "Authenticated users",
    menuHint: "Any signed-in user can access.",
    description: "Only signed-in product users can access this link.",
    detailLines: [
      "Requires trusted signed-in identity.",
      "Any authenticated user can start a chat.",
    ],
  },
  {
    value: EXTERNAL_CHAT_ACCESS_MODE_AUTHENTICATED_SELECTED_USERS,
    label: "Authenticated selected users",
    menuHint: "Only selected signed-in users can access.",
    description: "Only specific signed-in users can access this link.",
    detailLines: [
      "Requires trusted signed-in identity.",
      "User must be in the selected users list.",
    ],
  },
];

export const normalizeUniqueStringList = (values) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => String(value || "").trim())
        .filter((value) => value.length > 0)
    )
  );

export const periodUnitToSeconds = (unit) =>
  EXTERNAL_CHAT_RATE_LIMIT_PERIOD_UNITS.find((u) => u.value === unit)
    ?.seconds || 60;

export const splitPeriodSecondsIntoValueAndUnit = (totalSeconds) => {
  const seconds = Number(totalSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return {
      value: EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_VALUE,
      unit: EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_UNIT,
    };
  }
  for (const { value: unit, seconds: unitSeconds } of [
    ...EXTERNAL_CHAT_RATE_LIMIT_PERIOD_UNITS,
  ].reverse()) {
    if (seconds % unitSeconds === 0) {
      return { value: String(seconds / unitSeconds), unit };
    }
  }
  return {
    value: String(Math.max(1, Math.round(seconds / 60))),
    unit: "minutes",
  };
};

const normalizeExternalChatAccessMode = (value) => {
  const normalized = String(value || "")
    .trim()
    .toUpperCase();
  return EXTERNAL_CHAT_ACCESS_MODES.has(normalized)
    ? normalized
    : EXTERNAL_CHAT_ACCESS_MODE_DISABLED;
};

export const parseAccessAllowlistEmails = (value) =>
  Array.from(
    new Set(
      (Array.isArray(value) ? value : String(value || "").split(/[\n,]/))
        .flatMap((entry) => String(entry || "").split(/[\n,]/))
        .map((email) =>
          String(email || "")
            .trim()
            .toLowerCase()
        )
        .filter((email) => email.length > 0)
    )
  );

export const parseOptionalPositiveInt = (value) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.floor(parsed);
};

export function buildExternalAccessPolicy(accessPolicy) {
  const source =
    accessPolicy && typeof accessPolicy === "object" ? accessPolicy : {};
  const mode = normalizeExternalChatAccessMode(source.mode);
  const emailRestrictionRaw = String(source.emailRestriction || "ANY")
    .trim()
    .toUpperCase();
  const emailRestriction =
    emailRestrictionRaw === "ALLOWLIST" || emailRestrictionRaw === "DOMAIN"
      ? emailRestrictionRaw
      : "ANY";
  const allowedEmails = parseAccessAllowlistEmails(source.allowedEmails || []);
  const allowedDomains = parseAccessAllowlistEmails(
    source.allowedDomains || []
  );
  const selectedUserIds = normalizeUniqueStringList(
    source.selectedUserIds || []
  );

  return {
    mode,
    emailRestriction:
      mode === EXTERNAL_CHAT_ACCESS_MODE_EMAIL_MAGIC_LINK
        ? emailRestriction
        : "ANY",
    allowedEmails:
      mode === EXTERNAL_CHAT_ACCESS_MODE_EMAIL_MAGIC_LINK &&
      emailRestriction === "ALLOWLIST"
        ? allowedEmails
        : [],
    allowedDomains:
      mode === EXTERNAL_CHAT_ACCESS_MODE_EMAIL_MAGIC_LINK &&
      emailRestriction === "DOMAIN"
        ? allowedDomains
        : [],
    selectedUserIds:
      mode === EXTERNAL_CHAT_ACCESS_MODE_AUTHENTICATED_SELECTED_USERS
        ? selectedUserIds
        : [],
  };
}

export const buildNumericLimitSelectOptions = (
  presetValues,
  currentValue,
  noLimitLabel = "No limit"
) => {
  const normalizedCurrent = String(currentValue ?? "").trim();
  const baseOptions = [
    { value: "", label: noLimitLabel },
    ...presetValues.map((value) => ({
      value: String(value),
      label: String(value),
    })),
  ];
  if (
    normalizedCurrent &&
    !baseOptions.some((option) => option.value === normalizedCurrent)
  ) {
    return [
      {
        value: normalizedCurrent,
        label: `Current custom (${normalizedCurrent})`,
      },
      ...baseOptions,
    ];
  }
  return baseOptions;
};

export const getLimitOptionLabel = (options, value, fallback = "No limit") =>
  options.find((option) => option.value === String(value ?? ""))?.label ||
  fallback;
